import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Plus, Search, Pin, Archive, Trash2, Edit3, Copy, RefreshCw,
  Mic, MicOff, Volume2, Upload, X, FileText, Image, File, CheckSquare,
  AlertTriangle, Calendar, Shield, List, Loader2, Bot, User, MoreVertical,
  ChevronDown, Paperclip, StopCircle, Clock, Folder, Package, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('d3_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// ── Markdown Renderer (simple) ────────────────────────────────
const renderMarkdown = (text) => {
  if (!text) return '';
  // Handle tables
  let result = text.replace(/^(\|.+\|)\n(\|[-:| ]+\|)\n((?:\|.+\|\n?)*)/gm, (match, header, separator, body) => {
    const headers = header.split('|').filter(h => h.trim()).map(h => `<th class="px-3 py-1.5 text-left text-[11px] font-semibold text-zinc-300 border-b border-white/10">${h.trim()}</th>`).join('');
    const rows = body.trim().split('\n').map(row => {
      const cells = row.split('|').filter(c => c.trim()).map(c => `<td class="px-3 py-1.5 text-[11px] text-zinc-400 border-b border-white/5">${c.trim()}</td>`).join('');
      return `<tr class="hover:bg-white/[0.02]">${cells}</tr>`;
    }).join('');
    return `<div class="overflow-x-auto my-3"><table class="w-full border border-white/10 rounded-lg"><thead><tr class="bg-white/[0.03]">${headers}</tr></thead><tbody>${rows}</tbody></table></div>`;
  });
  return result
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-white/5 rounded-lg p-3 my-2 overflow-x-auto text-xs"><code>$2</code></pre>')
    .replace(/`([^`]+)`/g, '<code class="bg-white/10 px-1.5 py-0.5 rounded text-xs">$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^### (.+)$/gm, '<h3 class="text-sm font-semibold text-white mt-3 mb-1">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-semibold text-white mt-4 mb-1">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-lg font-bold text-white mt-4 mb-2">$1</h1>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal">$2</li>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
};

const PROMPT_ICONS = {
  'check-square': CheckSquare, 'alert-triangle': AlertTriangle,
  'file-text': FileText, 'search': Search, 'list': List,
  'calendar': Calendar, 'shield': Shield, 'edit': Edit3,
  'clock': Clock, 'folder': Folder, 'package': Package, 'bar-chart': BarChart3,
};

// ── Conversation List ─────────────────────────────────────────
const ConversationList = ({ conversations, activeId, onSelect, onNew, onDelete, onRename, onPin, onArchive, searchQuery, onSearch }) => {
  const [menuOpen, setMenuOpen] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');

  const pinned = conversations.filter(c => c.pinned);
  const recent = conversations.filter(c => !c.pinned);

  const handleRename = (conv) => {
    onRename(conv.id, renameVal);
    setRenaming(null);
  };

  return (
    <div className="flex flex-col h-full" data-testid="conversation-list">
      <div className="p-3 border-b border-white/5">
        <button
          onClick={onNew}
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors"
          data-testid="new-chat-btn"
        >
          <Plus className="w-4 h-4" /> New Chat
        </button>
      </div>
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-9 pr-3 py-2 text-xs bg-white/5 border border-white/5 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/30"
            data-testid="search-conversations"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
        {pinned.length > 0 && (
          <>
            <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Pinned</p>
            {pinned.map(c => (
              <ConvItem key={c.id} conv={c} active={c.id === activeId} onSelect={onSelect}
                menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming}
                renameVal={renameVal} setRenameVal={setRenameVal} handleRename={handleRename}
                onDelete={onDelete} onPin={onPin} onArchive={onArchive} />
            ))}
          </>
        )}
        {recent.length > 0 && (
          <>
            <p className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Recent</p>
            {recent.map(c => (
              <ConvItem key={c.id} conv={c} active={c.id === activeId} onSelect={onSelect}
                menuOpen={menuOpen} setMenuOpen={setMenuOpen} renaming={renaming} setRenaming={setRenaming}
                renameVal={renameVal} setRenameVal={setRenameVal} handleRename={handleRename}
                onDelete={onDelete} onPin={onPin} onArchive={onArchive} />
            ))}
          </>
        )}
        {conversations.length === 0 && (
          <p className="text-center text-zinc-600 text-xs py-8">No conversations yet</p>
        )}
      </div>
    </div>
  );
};

const ConvItem = ({ conv, active, onSelect, menuOpen, setMenuOpen, renaming, setRenaming, renameVal, setRenameVal, handleRename, onDelete, onPin, onArchive }) => (
  <div
    className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
      active ? 'bg-indigo-600/15 text-white' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'
    }`}
    onClick={() => onSelect(conv.id)}
    data-testid={`conv-${conv.id}`}
  >
    {conv.pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" />}
    {renaming === conv.id ? (
      <input
        autoFocus value={renameVal}
        onChange={e => setRenameVal(e.target.value)}
        onBlur={() => handleRename(conv)}
        onKeyDown={e => e.key === 'Enter' && handleRename(conv)}
        className="flex-1 text-xs bg-transparent border-b border-indigo-500 outline-none text-white"
        onClick={e => e.stopPropagation()}
      />
    ) : (
      <span className="flex-1 text-xs truncate">{conv.title}</span>
    )}
    <div className="relative">
      <button
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white/10 transition-opacity"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === conv.id ? null : conv.id); }}
      >
        <MoreVertical className="w-3 h-3" />
      </button>
      {menuOpen === conv.id && (
        <div className="absolute right-0 top-6 z-50 w-36 bg-[#111827] border border-white/10 rounded-lg shadow-xl py-1 text-xs">
          <button className="w-full px-3 py-1.5 text-left hover:bg-white/5 flex items-center gap-2"
            onClick={(e) => { e.stopPropagation(); setRenaming(conv.id); setRenameVal(conv.title); setMenuOpen(null); }}>
            <Edit3 className="w-3 h-3" /> Rename
          </button>
          <button className="w-full px-3 py-1.5 text-left hover:bg-white/5 flex items-center gap-2"
            onClick={(e) => { e.stopPropagation(); onPin(conv.id, !conv.pinned); setMenuOpen(null); }}>
            <Pin className="w-3 h-3" /> {conv.pinned ? 'Unpin' : 'Pin'}
          </button>
          <button className="w-full px-3 py-1.5 text-left hover:bg-white/5 flex items-center gap-2"
            onClick={(e) => { e.stopPropagation(); onArchive(conv.id); setMenuOpen(null); }}>
            <Archive className="w-3 h-3" /> Archive
          </button>
          <button className="w-full px-3 py-1.5 text-left hover:bg-white/5 text-red-400 flex items-center gap-2"
            onClick={(e) => { e.stopPropagation(); onDelete(conv.id); setMenuOpen(null); }}>
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      )}
    </div>
  </div>
);

// ── Message Bubble ────────────────────────────────────────────
const MessageBubble = ({ msg, onCopy, onRegenerate, isStreaming }) => {
  const isUser = msg.role === 'user';
  const [displayText, setDisplayText] = useState(isStreaming ? '' : msg.content);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!isStreaming || isUser) { setDisplayText(msg.content); return; }
    let idx = 0;
    const full = msg.content;
    const chunkSize = Math.max(2, Math.floor(full.length / 60));
    streamRef.current = setInterval(() => {
      idx = Math.min(idx + chunkSize, full.length);
      setDisplayText(full.slice(0, idx));
      if (idx >= full.length) clearInterval(streamRef.current);
    }, 20);
    return () => clearInterval(streamRef.current);
  }, [msg.content, isStreaming, isUser]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
      data-testid={`message-${msg.id}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        isUser ? 'bg-indigo-600/20' : 'bg-emerald-600/20'
      }`}>
        {isUser ? <User className="w-4 h-4 text-indigo-400" /> : <Bot className="w-4 h-4 text-emerald-400" />}
      </div>
      <div className={`max-w-[75%] ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block px-4 py-3 rounded-2xl text-sm leading-relaxed ${
          isUser
            ? 'bg-indigo-600/20 border border-indigo-500/20 text-zinc-200'
            : 'bg-white/[0.03] border border-white/[0.06] text-zinc-300'
        }`}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{displayText}</p>
          ) : (
            <div className="prose-invert" dangerouslySetInnerHTML={{ __html: renderMarkdown(displayText) }} />
          )}
        </div>
        {!isUser && !isStreaming && (
          <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity">
            <button onClick={() => onCopy(msg.content)} className="p-1 rounded hover:bg-white/5 text-zinc-600 hover:text-zinc-400" data-testid="copy-msg-btn">
              <Copy className="w-3 h-3" />
            </button>
            <button onClick={() => onRegenerate(msg.id)} className="p-1 rounded hover:bg-white/5 text-zinc-600 hover:text-zinc-400" data-testid="regen-msg-btn">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ── Main AI Assistant Screen ──────────────────────────────────
export default function AIAssistantScreen() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/conversations`, { headers: getAuthHeaders() });
      if (res.ok) setConversations(await res.json());
    } catch { /* silent */ }
  }, []);

  // Fetch messages
  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/conversations/${convId}/messages`, { headers: getAuthHeaders() });
      if (res.ok) setMessages(await res.json());
    } catch { /* silent */ }
  }, []);

  // Fetch prompts
  useEffect(() => {
    fetchConversations();
    fetch(`${API_URL}/api/ai/suggested-prompts`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(setSuggestedPrompts).catch(() => {});
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConvId) fetchMessages(activeConvId);
    else setMessages([]);
  }, [activeConvId, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Create new conversation
  const handleNewChat = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/conversations`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({}),
      });
      if (res.ok) {
        const conv = await res.json();
        setConversations(prev => [conv, ...prev]);
        setActiveConvId(conv.id);
        setMessages([]);
        setUploadedDocs([]);
      }
    } catch { toast.error('Failed to create conversation'); }
  };

  // Send message
  const handleSend = async (text) => {
    const content = text || input.trim();
    if (!content || isLoading) return;

    let convId = activeConvId;
    if (!convId) {
      try {
        const res = await fetch(`${API_URL}/api/ai/conversations`, {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({}),
        });
        if (res.ok) {
          const conv = await res.json();
          convId = conv.id;
          setConversations(prev => [conv, ...prev]);
          setActiveConvId(conv.id);
        }
      } catch { toast.error('Failed to create conversation'); return; }
    }

    setInput('');
    setIsLoading(true);

    // Optimistic user message
    const tempUserMsg = { id: 'temp-' + Date.now(), role: 'user', content, createdAt: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await fetch(`${API_URL}/api/ai/conversations/${convId}/messages`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ content, document_ids: uploadedDocs.map(d => d.id) }),
      });
      if (res.ok) {
        const data = await res.json();
        setStreamingMsgId(data.aiMessage.id);
        setMessages(prev => [...prev.filter(m => m.id !== tempUserMsg.id), data.userMessage, data.aiMessage]);
        fetchConversations();
        setUploadedDocs([]);
        setTimeout(() => setStreamingMsgId(null), Math.min(data.aiMessage.content.length * 20, 3000));
      } else {
        toast.error('Failed to get response');
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      }
    } catch { toast.error('Connection error'); }
    setIsLoading(false);
  };

  // Copy
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Regenerate
  const handleRegenerate = async (msgId) => {
    if (!activeConvId) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/conversations/${activeConvId}/regenerate/${msgId}`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const newMsg = await res.json();
        setMessages(prev => [...prev.filter(m => m.id !== msgId), newMsg]);
      }
    } catch { toast.error('Regeneration failed'); }
    setIsLoading(false);
  };

  // Conversation actions
  const handleDelete = async (id) => {
    await fetch(`${API_URL}/api/ai/conversations/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    setConversations(prev => prev.filter(c => c.id !== id));
    if (activeConvId === id) { setActiveConvId(null); setMessages([]); }
    toast.success('Conversation deleted');
  };

  const handleRename = async (id, title) => {
    await fetch(`${API_URL}/api/ai/conversations/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ title }),
    });
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
  };

  const handlePin = async (id, pinned) => {
    await fetch(`${API_URL}/api/ai/conversations/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ pinned }),
    });
    setConversations(prev => prev.map(c => c.id === id ? { ...c, pinned } : c));
  };

  const handleArchive = async (id) => {
    await fetch(`${API_URL}/api/ai/conversations/${id}`, {
      method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ archived: true }),
    });
    setConversations(prev => prev.filter(c => c.id !== id));
    if (activeConvId === id) { setActiveConvId(null); setMessages([]); }
    toast.success('Conversation archived');
  };

  // Search
  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { fetchConversations(); return; }
    try {
      const res = await fetch(`${API_URL}/api/ai/search?q=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
      if (res.ok) setConversations(await res.json());
    } catch { /* silent */ }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      if (activeConvId) formData.append('conversation_id', activeConvId);
      try {
        const token = localStorage.getItem('d3_token');
        const res = await fetch(`${API_URL}/api/ai/documents/upload`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
        });
        if (res.ok) {
          const doc = await res.json();
          setUploadedDocs(prev => [...prev, doc]);
          toast.success(`${file.name} uploaded`);
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      } catch { toast.error(`Upload error: ${file.name}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        try {
          const token = localStorage.getItem('d3_token');
          const res = await fetch(`${API_URL}/api/ai/voice/transcribe`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData,
          });
          if (res.ok) {
            const { text } = await res.json();
            setInput(prev => prev + text);
            toast.success('Voice transcribed');
          } else { toast.error('Transcription failed'); }
        } catch { toast.error('Transcription error'); }
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const activeConv = conversations.find(c => c.id === activeConvId);

  return (
    <div className="flex h-full overflow-hidden bg-[#0a0e1a]" data-testid="ai-assistant-screen">
      {/* Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ width: 0, opacity: 0 }} animate={{ width: 280, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
            className="h-full border-r border-white/5 bg-[#080c16] overflow-hidden shrink-0"
          >
            <ConversationList
              conversations={conversations} activeId={activeConvId}
              onSelect={setActiveConvId} onNew={handleNewChat}
              onDelete={handleDelete} onRename={handleRename}
              onPin={handlePin} onArchive={handleArchive}
              searchQuery={searchQuery} onSearch={handleSearch}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="h-12 border-b border-white/5 flex items-center px-4 gap-3 shrink-0">
          <button onClick={() => setShowSidebar(!showSidebar)} className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-400">
            {showSidebar ? <ChevronDown className="w-4 h-4 rotate-90" /> : <ChevronDown className="w-4 h-4 -rotate-90" />}
          </button>
          <Bot className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-medium text-white truncate">
            {activeConv?.title || 'D3 Command AI'}
          </span>
          <span className="ml-auto text-[10px] text-zinc-600">GPT-5.2</span>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
          {messages.length === 0 && !activeConvId ? (
            // Welcome screen with suggested prompts
            <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600/20 to-emerald-600/20 border border-white/10 flex items-center justify-center mb-6">
                <Bot className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">D3 Command AI</h2>
              <p className="text-sm text-zinc-500 mb-8 text-center max-w-md">
                Your enterprise assistant for project management, document analysis, risk assessment, and more.
              </p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
                {suggestedPrompts.map((p, i) => {
                  const Icon = PROMPT_ICONS[p.icon] || FileText;
                  return (
                    <button
                      key={i}
                      onClick={() => handleSend(p.text)}
                      className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-left text-xs text-zinc-400 hover:bg-white/[0.06] hover:text-zinc-200 transition-colors"
                      data-testid={`prompt-${i}`}
                    >
                      <Icon className="w-4 h-4 text-indigo-400 shrink-0" />
                      <span className="line-clamp-2">{p.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-zinc-600 text-sm">
              <Bot className="w-10 h-10 mb-3 text-zinc-700" />
              <p>Start a conversation...</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="group">
                <MessageBubble msg={msg} onCopy={handleCopy} onRegenerate={handleRegenerate} isStreaming={msg.id === streamingMsgId} />
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-zinc-600 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Uploaded docs preview */}
        {uploadedDocs.length > 0 && (
          <div className="px-4 pb-2 flex gap-2 flex-wrap">
            {uploadedDocs.map(doc => (
              <div key={doc.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600/10 border border-indigo-500/20 text-xs text-indigo-300">
                <Paperclip className="w-3 h-3" />
                <span className="truncate max-w-[120px]">{doc.originalFilename}</span>
                <button onClick={() => setUploadedDocs(prev => prev.filter(d => d.id !== doc.id))} className="hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-white/5 p-4 shrink-0">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-2 bg-white/[0.03] border border-white/[0.08] rounded-2xl px-4 py-3 focus-within:border-indigo-500/30 transition-colors">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-zinc-300 transition-colors shrink-0"
                title="Upload document"
                data-testid="upload-btn"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
              <input
                ref={fileInputRef} type="file" className="hidden" multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg"
                onChange={handleFileUpload}
              />
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Ask D3 Command AI anything..."
                className="flex-1 bg-transparent text-sm text-white placeholder-zinc-600 resize-none outline-none max-h-32 min-h-[20px]"
                rows={1}
                data-testid="chat-input"
              />
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                  isRecording ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'hover:bg-white/5 text-zinc-500 hover:text-zinc-300'
                }`}
                title={isRecording ? 'Stop recording' : 'Voice input'}
                data-testid="voice-btn"
              >
                {isRecording ? <StopCircle className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <button
                onClick={() => handleSend()}
                disabled={(!input.trim() && uploadedDocs.length === 0) || isLoading}
                className="p-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors shrink-0"
                data-testid="send-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[10px] text-zinc-700 text-center mt-2">
              D3 Command AI may make mistakes. Verify important information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
